import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '../../api/client'
import type { EagleTop, TopologyEdge, TopologyNode } from '../../api/types'
import { usePolling } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { FAMILY_TYPES, gruppoDi, livelloDi, nomeFascia } from '../../pages/Topology'
import { etichettaStato, tonoStato } from '../EagleStato'
import { Widget } from '../Griglia'
import Mappa, { type ArcoMappa, type NodoMappa, type VoceLegenda } from '../Mappa'
import type { LayoutMappa, Posizione } from '../mappaLayout'
import { stessaPorta } from './SceltaLink'

/**
 * A saved topology view, coloured by Eagle.
 *
 * The view is the one people save in Rete → Mappa: its filters, focus and
 * depth decide which graph is asked of the server, its pinned positions are
 * kept (`fissati`). On top of it: every node takes the colour of its worst
 * parameter state (grey = not monitored), every LLDP/CDP edge takes the
 * colour of the worst of its two interfaces and a thickness proportional to
 * their utilisation — matched by port name (`local_port`, `remote_port`)
 * against the interfaces Eagle reads. Click a node → the CI's Eagle section.
 *
 * Three readings, all cached on the server and shared with the other
 * widgets: the graph (every 5 minutes), the overview (states), the
 * interface ranking without sparkline (current values). Never a call per CI.
 */

type RigaTop = EagleTop['rows'][number]
type Tono = ReturnType<typeof tonoStato>

const COLORE: Record<Tono, string> = { ok: 'var(--ok)', warn: 'var(--warn)', error: 'var(--error)', muted: 'var(--text-muted)' }
const GRAVITA: Record<string, number> = { down: 4, crit: 3, warn: 2, ok: 1 }
const peggiore = (...stati: (string | null | undefined)[]) =>
  stati.reduce<string | null>((acc, s) => ((GRAVITA[s ?? ''] ?? 0) > (GRAVITA[acc ?? ''] ?? 0) ? (s ?? null) : acc), null)
const comeBool = (v: unknown, predefinito: boolean) => (v == null || v === '' ? predefinito : v === true || v === 'true')

function portaDi(righe: RigaTop[] | undefined, porta: string): RigaTop | undefined {
  if (!righe || !porta) return undefined
  return righe.find((r) => stessaPorta(r.name, porta))
}

export function MappaEagle({ options, sinottico }: { options: Record<string, unknown>; sinottico: boolean }) {
  const viewId = String(options.view_id ?? '')
  const coloraArchi = comeBool(options.archi, true)
  const alta = String(options.altezza ?? 'normale') === 'alta'
  const navigate = useNavigate()

  const viste = usePolling(() => api.topologyViews(), 5 * 60_000)
  const vista = useMemo(() => viste.data?.find((v) => v.id === viewId) ?? null, [viste.data, viewId])
  const cfg = vista?.config ?? {}
  const family = typeof cfg.family === 'string' ? cfg.family : 'all'
  const relTypes = family !== 'all' && family !== 'other' ? FAMILY_TYPES[family] : undefined
  const focus = typeof cfg.focus === 'string' ? cfg.focus : null
  const depth = cfg.depth === 2 ? 2 : 1
  const grafo = usePolling(
    () => (vista ? api.topology({ relTypes, focus, depth }) : Promise.resolve(null)),
    5 * 60_000, [viewId, vista?.updated_at ?? '', family, focus, depth],
  )
  const overview = usePolling(() => api.eagleOverview(6), 60_000)
  // Le interfacce senza sparkline (hours=0): servono solo i valori attuali.
  const interfacce = usePolling(() => api.eagleTop('interface', 400, 0), 60_000)

  // Il layout della vista: le vecchie dicono «ring»/«force» (tipo stretto), le
  // nuove gerarchico/radiale/organico — si legge come testo, come fa Rete → Mappa.
  const layoutVista = String((cfg as { layout?: string }).layout ?? '')
  const [layout, setLayout] = useState<LayoutMappa>(
    layoutVista === 'radiale' || layoutVista === 'organico' || layoutVista === 'gerarchico' ? (layoutVista as LayoutMappa) : 'gerarchico',
  )

  const { nodi, archi, nNodi, nArchi, fissati, nonMonitorati } = useMemo(() => {
    const stati = new Map((overview.data?.licensed ? overview.data.cis : []).map((c) => [c.ci_id, c.status]))
    const perCI = new Map<string, RigaTop[]>()
    for (const r of interfacce.data?.rows ?? []) perCI.set(r.ci_id, [...(perCI.get(r.ci_id) ?? []), r])
    const nascosti = new Set(Array.isArray(cfg.hidden_types) ? cfg.hidden_types : [])
    const nodes: TopologyNode[] = (grafo.data?.nodes ?? []).filter((n) => !nascosti.has(n.ci_class ?? n.ci_type))
    const ids = new Set(nodes.map((n) => n.id))
    const edges: TopologyEdge[] = (grafo.data?.edges ?? []).filter((e) => ids.has(e.source) && ids.has(e.target))
    const nome = new Map(nodes.map((n) => [n.id, n.name]))
    let nonMonitorati = 0
    const nodi: NodoMappa[] = nodes.map((n) => {
      const st = stati.get(n.id)
      if (!st) nonMonitorati++
      const tono: Tono | null = st ? tonoStato(st) : null
      return {
        id: n.id, label: n.name,
        sotto: st ? etichettaStato(st) : t('non monitorato'),
        colore: tono ? COLORE[tono] : 'var(--divider)',
        gruppo: gruppoDi(n), livello: livelloDi(n),
        seme: n.id === focus, spento: n.lifecycle_state === 'retired',
      }
    })
    const archi: ArcoMappa[] = edges.map((e) => {
      const lp = String(e.attributes?.local_port ?? '')
      const rp = String(e.attributes?.remote_port ?? '')
      const a = portaDi(perCI.get(e.source), lp)
      const b = portaDi(perCI.get(e.target), rp)
      const utilizzi = [a, b].filter((r): r is RigaTop => !!r && r.unit === '%' && r.value != null).map((r) => r.value as number)
      const util = utilizzi.length ? Math.max(...utilizzi) : null
      const stato = peggiore(a?.status, b?.status)
      const tono: Tono | null = coloraArchi && stato ? tonoStato(stato) : null
      const capi = [lp ? `${nome.get(e.source) ?? ''} ${lp}` : nome.get(e.source) ?? '', rp ? `${nome.get(e.target) ?? ''} ${rp}` : nome.get(e.target) ?? '']
      const titolo = `${capi.join(' ↔ ')}${util != null ? ` · ${Math.round(util)} %` : ''}${!a && !b ? ` · ${t('interfacce non monitorate')}` : stato ? ` · ${etichettaStato(stato)}` : ''}`
      return {
        id: e.id, src: e.source, dst: e.target,
        colore: tono && tono !== 'muted' ? COLORE[tono] : 'var(--border)',
        spessore: util != null ? 1 + Math.round((4 * Math.min(100, util)) / 100) : 1,
        etichetta: util != null ? `${Math.round(util)} %` : undefined,
        titolo,
        tratteggio: e.rel_type === 'talks_to' || e.rel_type === 'depends_on' ? '4 3' : undefined,
      }
    })
    // Le posizioni fissate nella vista; quelle delle viste vecchie («ring»/«force»,
    // misurate su un'altra tela) non si riapplicano, come in Rete → Mappa.
    const legacy = layoutVista === 'ring' || layoutVista === 'force'
    const fissati = new Map<string, Posizione>(legacy ? [] : Object.entries(vista?.positions ?? {}))
    return { nodi, archi, nNodi: nodes.length, nArchi: edges.length, fissati, nonMonitorati }
  }, [grafo.data, overview.data, interfacce.data, cfg.hidden_types, layoutVista, vista?.positions, focus, coloraArchi])

  const legenda: VoceLegenda[] = [
    { etichetta: t('ok'), colore: COLORE.ok, forma: 'punto' },
    { etichetta: t('attenzione'), colore: COLORE.warn, forma: 'punto' },
    { etichetta: t('critico o non risponde'), colore: COLORE.error, forma: 'punto' },
    { etichetta: t('non monitorato'), colore: 'var(--divider)', forma: 'punto' },
    ...(coloraArchi ? [{ etichetta: t('arco: stato peggiore dei due capi, spessore = utilizzo'), colore: 'var(--border)', forma: 'linea' as const }] : []),
  ]
  const caricando = viste.loading || grafo.loading || overview.loading || interfacce.loading
  const errore = viste.error ?? grafo.error ?? overview.error ?? interfacce.error

  return (
    <Widget titolo={t('Mappa con lo stato')}
            sotto={vista ? t('{v} · {n} nodi · {m} collegamenti{extra}', { v: vista.name, n: nNodi, m: nArchi, extra: nonMonitorati ? ` · ${t('{k} non monitorati', { k: nonMonitorati })}` : '' }) : undefined}
            caricando={caricando} errore={errore} link={<Link to="/topology">{t('apri la mappa')}</Link>}>
      {!viewId ? (
        <p className="muted" style={{ margin: 0 }}>{t('Scegli una vista di topologia salvata in «Titolo e opzioni» (in modifica). Le viste si creano in Rete → Mappa.')}</p>
      ) : viste.data && !vista ? (
        <p className="muted" style={{ margin: 0 }}>{t('La vista salvata non esiste più: scegline un’altra in «Titolo e opzioni».')}</p>
      ) : grafo.data && vista ? (
        nodi.length === 0 ? <p className="muted" style={{ margin: 0 }}>{t('La vista non ha nodi con questi filtri.')}</p> : (
          <>
            <Mappa nodi={nodi} archi={archi} layout={layout} onLayout={setLayout} fissati={fissati} nomiFasce={nomeFascia}
                   legenda={legenda} etichetta={t('Mappa «{v}» con lo stato di Eagle', { v: vista.name })}
                   altezza={sinottico ? 560 : alta ? 520 : 262}
                   onClickNodo={(id) => navigate(`/cis/${id}#eagle`)} onDoppioClickNodo={(id) => navigate(`/cis/${id}#eagle`)} />
            {grafo.data.truncated && <p className="muted eagle-piccolo" style={{ margin: '4px 0 0' }}>{t('Mappa parziale: {n} collegamenti in tutto, qui i primi.', { n: grafo.data.total_edges })}</p>}
          </>
        )
      ) : null}
    </Widget>
  )
}
